import React from 'react';
import { Button } from '@getro/rombo';
import { useGoogleLogin } from '@react-oauth/google';
import PropTypes from 'prop-types';
import { Box } from 'rebass/styled-components';

import { useSignIn } from '../../../hooks/useSignIn';
import { useSignUp } from '../../../hooks/useSignUp';
import { GoogleIcon } from './components/googleIcon';

export const GoogleSignInButton = ({
  containerProps,
  mode,
  params,
  onSuccess,
  onError,
  isInvite,
  isLoading,
  ...buttonProps
}) => {
  const { signIn, isLoading: isLoadingSignIn } = useSignIn({ isInvite });
  const { signUp, isLoading: isLoadingSignUp } = useSignUp();

  const onSuccessResponse = (tokenResponse) => {
    const { code } = tokenResponse;
    const method = mode === 'sign-in' ? signIn : signUp;

    method({ code, identityProvider: 'google_oauth2', ...params })
      .then((result) => {
        // When MFA is required the user is mid-redirect to the challenge; don't fire
        // onSuccess (which would close the auth flow before the session exists).
        if (result?.mfaRequired || result?.mfaSetupRequired) return;
        onSuccess(result);
      })
      .catch((error) => onError({ ...error, provider: 'google_oauth2' }));
  };

  const onErrorResponse = (error) => {
    onError({ ...error, provider: 'google_oauth2' });
  };

  const googleSignIn = useGoogleLogin({
    onSuccess: onSuccessResponse,
    onError: onErrorResponse,
    flow: 'auth-code',
    redirect_uri: 'postmessage',
  });

  return (
    <Box {...containerProps}>
      <Button
        variant="secondary"
        icon={() => <GoogleIcon width="16px" height="16px" />}
        iconGap={2}
        width="100%"
        loading={isLoadingSignIn || isLoadingSignUp || isLoading}
        sx={{ alignItems: 'center', justifyContent: 'center' }}
        {...buttonProps}
        onClick={googleSignIn}
      >
        Continue with Google
      </Button>
    </Box>
  );
};

GoogleSignInButton.propTypes = {
  containerProps: PropTypes.object,
  mode: PropTypes.oneOf(['sign-in', 'sign-up']),
  params: PropTypes.object,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  isInvite: PropTypes.bool,
  isLoading: PropTypes.bool,
};

GoogleSignInButton.defaultProps = {
  containerProps: {},
  mode: 'sign-in',
  params: {},
  onSuccess: () => {},
  onError: () => {},
  isInvite: false,
  isLoading: false,
};
